// src/components/PortalHeader.jsx
// Floating pill header shared across role dashboards, matching the marketing site.
// Desktop: logo + subtle role | nav (center) | theme, sign out (right).
// Mobile: logo + role | theme + hamburger; nav + sign out drop down below.

import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ThemeToggle from "./ThemeToggle";
import { LogOut, Menu, X } from "./icons";

const ROLE_LABEL = {
  admin: "Admin",
  company: "Company",
  practitioner: "Practitioner",
  user: "Personal",
};

export default function PortalHeader({ nav = [] }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onSignOut = async () => {
    setOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };

  const linkClass = ({ isActive }) =>
    `inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
      isActive
        ? "bg-tb-brand/10 text-tb-brand dark:bg-tb-brand/15"
        : "text-tb-muted hover:text-tb-ink dark:hover:text-tb-d-text hover:bg-tb-bg-2 dark:hover:bg-tb-d-inset"
    }`;

  return (
    <header className="sticky top-0 z-40">
      <div
        className="mx-auto max-w-6xl px-4 pt-4"
        // add iOS safe-area inset so the pill clears the island/notch
        style={{ paddingTop: "calc(1rem + env(safe-area-inset-top))" }}
      >
        <div className="flex items-center justify-between gap-3 h-14 pl-4 pr-2.5
                        rounded-full border border-tb-border dark:border-tb-d-border
                        bg-white/80 dark:bg-tb-d-card/80 backdrop-blur-md shadow-sm">

          {/* left: logo + subtle role */}
          <div className="flex items-center gap-2 min-w-0">
                       <img src={process.env.PUBLIC_URL + "/logo-light.png"} alt="taxpack" className="h-7 w-auto shrink-0 dark:hidden" />
            <img src={process.env.PUBLIC_URL + "/logo-dark.png"} alt="taxpack" className="h-7 w-auto shrink-0 hidden dark:block" />
            <span className="hidden sm:inline text-[10px] text-tb-muted/60 shrink-0">
              {ROLE_LABEL[user?.role] || user?.role}
            </span>
          </div>

          {/* center: nav */}
          <nav className="hidden md:flex items-center gap-1">
            {nav.map(({ label, path, icon: Icon }) => (
              <NavLink key={path} to={path} end={path === nav[0]?.path} className={linkClass}>
                {Icon && <Icon size={16} />}
                {label}
              </NavLink>
            ))}
          </nav>

          {/* right: desktop controls */}
          <div className="hidden md:flex items-center gap-1.5 shrink-0">
            <span className="hidden lg:block text-sm text-tb-muted max-w-[140px] truncate">{user?.name}</span>
            <ThemeToggle />
            <button
              onClick={onSignOut}
              className="grid place-items-center h-9 w-9 rounded-full text-tb-muted hover:text-tb-brand
                         hover:bg-tb-bg-2 dark:hover:bg-tb-d-inset transition-colors"
              title="Sign out"
            >
              <LogOut size={16} />
            </button>
          </div>

          {/* right: mobile controls */}
          <div className="flex md:hidden items-center gap-1.5 shrink-0">
            <ThemeToggle />
            <button
              onClick={() => setOpen((v) => !v)}
              className="grid place-items-center h-9 w-9 rounded-full text-tb-muted hover:text-tb-brand
                         hover:bg-tb-bg-2 dark:hover:bg-tb-d-inset transition-colors"
              aria-label="Toggle menu"
              aria-expanded={open}
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* mobile dropdown */}
        {open && (
          <nav className="md:hidden mt-2 p-2 rounded-2xl border border-tb-border dark:border-tb-d-border
                          bg-white/95 dark:bg-tb-d-card/95 backdrop-blur-md shadow-sm flex flex-col gap-0.5">
            {nav.map(({ label, path, icon: Icon }) => (
              <NavLink key={path} to={path} end={path === nav[0]?.path} onClick={() => setOpen(false)} className={linkClass}>
                {Icon && <Icon size={16} />}
                {label}
              </NavLink>
            ))}
            <div className="mt-1 pt-2 border-t border-tb-border dark:border-tb-d-border flex items-center justify-between gap-3 px-2 pb-1">
              <span className="text-sm text-tb-muted truncate">{user?.name}</span>
              <button
                onClick={onSignOut}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-tb-muted
                           hover:text-tb-brand hover:bg-tb-bg-2 dark:hover:bg-tb-d-inset transition-colors shrink-0"
              >
                <LogOut size={16} /> Sign out
              </button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}